'use client';

import { useState, useEffect } from 'react';
import { 
  FileSignature, 
  Plus, 
  Edit2, 
  Trash2, 
  CheckCircle, 
  Clock, 
  Archive,
  Save,
  Link2,
  Loader2,
  X,
  Search,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import {
  useContracts,
  useCreateContract,
  useUpdateContract,
  useUpdateContractStatus,
  useLinkUseCase,
  useDeleteContract,
  type Contract,
} from '@/hooks/use-contracts';
import { useDefinition } from '@/hooks/use-definition';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

interface ContractEditorProps {
  projectId: string;
  projectName?: string;
}

const STATUS_CONFIG = {
  DRAFT: { label: 'Draft', color: 'bg-gray-100 text-gray-800', icon: FileSignature },
  IN_REVIEW: { label: 'In Review', color: 'bg-blue-100 text-blue-800', icon: Clock },
  APPROVED: { label: 'Approved', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  ARCHIVED: { label: 'Archived', color: 'bg-gray-100 text-gray-600', icon: Archive },
};

const VALID_TRANSITIONS: Record<string, string[]> = {
  DRAFT: ['IN_REVIEW'],
  IN_REVIEW: ['APPROVED', 'DRAFT'],
  APPROVED: ['ARCHIVED', 'DRAFT'],
  ARCHIVED: ['DRAFT'],
};

export function ContractEditor({ projectId, projectName = 'Project' }: ContractEditorProps) {
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingContract, setEditingContract] = useState<Contract | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  
  const { toast } = useToast();
  const { data: contracts, isLoading } = useContracts(projectId);
  const createMutation = useCreateContract(projectId);
  const updateMutation = useUpdateContract(projectId);
  const statusMutation = useUpdateContractStatus(projectId);
  const deleteMutation = useDeleteContract(projectId);

  const handleCreate = async (data: Partial<Contract>) => {
    try {
      await createMutation.mutateAsync(data);
      toast({ title: 'Contract created', description: 'Contract has been created successfully.' });
      setShowCreateDialog(false);
    } catch (error) {
      toast({ 
        title: 'Creation failed', 
        description: error instanceof Error ? error.message : 'Failed to create contract',
        variant: 'destructive',
      });
    }
  };

  const handleUpdate = async (contractId: string, data: Partial<Contract>) => {
    try {
      await updateMutation.mutateAsync({ contractId, data });
      toast({ title: 'Contract updated', description: 'Contract has been updated successfully.' });
      setEditingContract(null);
    } catch (error) {
      toast({ 
        title: 'Update failed', 
        description: error instanceof Error ? error.message : 'Failed to update contract',
        variant: 'destructive',
      });
    }
  };

  const handleStatusChange = async (contractId: string, newStatus: Contract['status']) => {
    try {
      await statusMutation.mutateAsync({ contractId, status: newStatus });
      toast({ title: 'Status updated', description: `Contract status changed to ${newStatus}.` });
    } catch (error) {
      toast({ 
        title: 'Status update failed', 
        description: error instanceof Error ? error.message : 'Failed to update status',
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async (contractId: string) => {
    try {
      await deleteMutation.mutateAsync(contractId);
      toast({ title: 'Contract deleted', description: 'Contract has been deleted successfully.' });
      setShowDeleteConfirm(null);
    } catch (error) {
      toast({ 
        title: 'Deletion failed', 
        description: error instanceof Error ? error.message : 'Failed to delete contract',
        variant: 'destructive',
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="border-b bg-white px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Contracts</h1>
            <p className="text-sm text-gray-600 mt-1">Manage API and data contracts for {projectName}</p>
          </div>
          <Button onClick={() => setShowCreateDialog(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Create Contract
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {!contracts || contracts.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <FileSignature className="w-16 h-16 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Contracts</h3>
            <p className="text-gray-600 mb-6">Create your first contract to get started.</p>
            <Button onClick={() => setShowCreateDialog(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Create Contract
            </Button>
          </div>
        ) : (
          <div className="grid gap-4">
            {contracts.map((contract) => {
              const statusConfig = STATUS_CONFIG[contract.status];
              const StatusIcon = statusConfig.icon;
              const availableTransitions = VALID_TRANSITIONS[contract.status] || [];
              
              return (
                <Card key={contract.id} className="hover:shadow-md transition-shadow">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <CardTitle className="text-lg">{contract.title}</CardTitle>
                          <Badge className={cn(statusConfig.color, 'flex items-center gap-1')}>
                            <StatusIcon className="w-3 h-3" />
                            {statusConfig.label}
                          </Badge>
                        </div>
                        <CardDescription>Version {contract.version}</CardDescription>
                        {contract.description && (
                          <p className="text-sm text-gray-600 mt-2">{contract.description}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setEditingContract(contract)}
                        >
                          <Edit2 className="w-4 h-4 mr-2" />
                          Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowDeleteConfirm(contract.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {contract.linkedUseCases && contract.linkedUseCases.length > 0 && (
                        <div>
                          <h4 className="text-sm font-medium text-gray-700 mb-2">Linked Use Cases</h4>
                          <div className="flex flex-wrap gap-2">
                            {contract.linkedUseCases.map((uc) => (
                              <Badge key={uc.id} variant="outline">
                                <Link2 className="w-3 h-3 mr-1" />
                                {uc.title}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {availableTransitions.length > 0 && (
                        <div className="flex items-center gap-2 pt-2 border-t">
                          <span className="text-sm text-gray-600">Change status:</span>
                          {availableTransitions.map((transition) => (
                            <Button
                              key={transition}
                              variant="outline"
                              size="sm"
                              onClick={() => handleStatusChange(contract.id, transition as Contract['status'])}
                              disabled={statusMutation.isPending}
                            >
                              {STATUS_CONFIG[transition as keyof typeof STATUS_CONFIG].label}
                            </Button>
                          ))}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <ContractDialog
        isOpen={showCreateDialog || editingContract !== null}
        onClose={() => {
          setShowCreateDialog(false);
          setEditingContract(null);
        }}
        onSave={editingContract 
          ? (data) => handleUpdate(editingContract.id, data)
          : handleCreate
        }
        contract={editingContract}
        isLoading={createMutation.isPending || updateMutation.isPending}
        projectId={projectId}
      />

      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <Dialog open={!!showDeleteConfirm} onOpenChange={() => setShowDeleteConfirm(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Contract</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-gray-600">
              Are you sure you want to delete this contract? This action cannot be undone.
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDeleteConfirm(null)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => handleDelete(showDeleteConfirm)}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

interface ContractDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: Partial<Contract>) => Promise<void>;
  contract?: Contract | null;
  isLoading?: boolean;
  projectId: string;
}

// Type definitions for structured JSON fields
type ApiEndpoint = {
  path: string;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  description?: string;
  requestSchema?: string;
  responseSchema?: string;
};

type DataEntity = {
  name: string;
  fields: Array<{
    name: string;
    type: string;
    required: boolean;
    validation?: string;
  }>;
};

type SLATerm = {
  metric: string;
  target: string;
  unit?: string;
  measurement?: string;
};

function ContractDialog({ isOpen, onClose, onSave, contract, isLoading, projectId }: ContractDialogProps) {
  const { toast } = useToast();
  const { data: useCasesArtefact } = useDefinition(projectId, 'use-cases');
  const useCases = useCasesArtefact?.items;
  const linkUseCaseMutation = useLinkUseCase(projectId);
  
  // Helper to safely parse JSON fields
  const parseJsonField = <T,>(value: Record<string, unknown> | null | undefined, defaultValue: T[]): T[] => {
    if (!value) return defaultValue;
    if (Array.isArray(value)) return value as T[];
    return defaultValue;
  };

  const [formData, setFormData] = useState<Partial<Contract>>({
    title: contract?.title || '',
    version: contract?.version || '1.0.0',
    description: contract?.description || '',
    baseSpecId: contract?.baseSpecId || undefined,
    apiContract: contract?.apiContract || null,
    dataContract: contract?.dataContract || null,
    slaTerms: contract?.slaTerms || null,
  });

  // Local state for structured fields
  const [apiEndpoints, setApiEndpoints] = useState<ApiEndpoint[]>(() => {
    const apiContract = contract?.apiContract as Record<string, unknown> | null;
    if (apiContract && Array.isArray(apiContract.endpoints)) {
      return apiContract.endpoints as ApiEndpoint[];
    }
    return [];
  });
  
  const [dataEntities, setDataEntities] = useState<DataEntity[]>(() => {
    const dataContract = contract?.dataContract as Record<string, unknown> | null;
    if (dataContract && Array.isArray(dataContract.entities)) {
      return dataContract.entities as DataEntity[];
    }
    return [];
  });
  
  const [slaTerms, setSlaTerms] = useState<SLATerm[]>(() => {
    const sla = contract?.slaTerms as Record<string, unknown> | null;
    if (sla && Array.isArray(sla.terms)) {
      return sla.terms as SLATerm[];
    }
    return [];
  });

  // Use case linking state
  const [selectedUseCaseIds, setSelectedUseCaseIds] = useState<string[]>(() => 
    contract?.linkedUseCases?.map(uc => uc.id) || []
  );
  const [useCaseSearch, setUseCaseSearch] = useState('');

  // Reset state when dialog opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        title: contract?.title || '',
        version: contract?.version || '1.0.0',
        description: contract?.description || '',
        baseSpecId: contract?.baseSpecId || undefined,
        apiContract: contract?.apiContract || null,
        dataContract: contract?.dataContract || null,
        slaTerms: contract?.slaTerms || null,
      });
      
      const apiContract = contract?.apiContract as Record<string, unknown> | null;
      setApiEndpoints(apiContract && Array.isArray(apiContract.endpoints) 
        ? (apiContract.endpoints as ApiEndpoint[]) 
        : []);
      
      const dataContract = contract?.dataContract as Record<string, unknown> | null;
      setDataEntities(dataContract && Array.isArray(dataContract.entities) 
        ? (dataContract.entities as DataEntity[]) 
        : []);
      
      const sla = contract?.slaTerms as Record<string, unknown> | null;
      setSlaTerms(sla && Array.isArray(sla.terms) 
        ? (sla.terms as SLATerm[]) 
        : []);
      
      setSelectedUseCaseIds(contract?.linkedUseCases?.map(uc => uc.id) || []);
      setUseCaseSearch('');
    }
  }, [isOpen, contract]);

  // Filter use cases by search
  const filteredUseCases = useCases?.filter(uc => 
    uc.title.toLowerCase().includes(useCaseSearch.toLowerCase()) ||
    uc.code?.toLowerCase().includes(useCaseSearch.toLowerCase())
  ) || [];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const dataToSave: Partial<Contract> = {
      ...formData,
      apiContract: apiEndpoints.length > 0 ? { endpoints: apiEndpoints } : null,
      dataContract: dataEntities.length > 0 ? { entities: dataEntities } : null,
      slaTerms: slaTerms.length > 0 ? { terms: slaTerms } : null,
    };
    
    await onSave(dataToSave);
    
    // Handle use case linking after save (if contract exists)
    if (contract?.id) {
      const currentLinkedIds = contract.linkedUseCases?.map(uc => uc.id) || [];
      const toLink = selectedUseCaseIds.filter(id => !currentLinkedIds.includes(id));
      const toUnlink = currentLinkedIds.filter(id => !selectedUseCaseIds.includes(id));
      
      // Link new use cases
      for (const useCaseId of toLink) {
        try {
          await linkUseCaseMutation.mutateAsync({ contractId: contract.id, useCaseId });
        } catch (error) {
          toast({
            title: 'Failed to link use case',
            description: error instanceof Error ? error.message : 'Unknown error',
            variant: 'destructive',
          });
        }
      }
      
      // Note: Unlinking would require a DELETE endpoint - for now, we'll just link new ones
      // The backend should handle this via the link endpoint (toggle behavior)
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{contract ? 'Edit Contract' : 'Create Contract'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-700">Title *</label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              className="mt-1"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Version</label>
            <Input
              value={formData.version}
              onChange={(e) => setFormData({ ...formData, version: e.target.value })}
              className="mt-1"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Description</label>
            <Textarea
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className="mt-1"
            />
          </div>

          {/* Use Case Linking */}
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">Linked Use Cases</label>
            <div className="space-y-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search use cases..."
                  value={useCaseSearch}
                  onChange={(e) => setUseCaseSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
              <div className="border rounded-lg p-2 max-h-40 overflow-y-auto">
                {filteredUseCases.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-2">No use cases found</p>
                ) : (
                  <div className="space-y-1">
                    {filteredUseCases.map((uc) => (
                      <label
                        key={uc.id}
                        className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedUseCaseIds.includes(uc.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedUseCaseIds([...selectedUseCaseIds, uc.id]);
                            } else {
                              setSelectedUseCaseIds(selectedUseCaseIds.filter(id => id !== uc.id));
                            }
                          }}
                          className="rounded"
                        />
                        <span className="text-sm flex-1">
                          {uc.code && <span className="font-mono text-gray-500 mr-2">{uc.code}</span>}
                          {uc.title}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              {selectedUseCaseIds.length > 0 && (
                <div className="flex flex-wrap gap-2 pt-2">
                  {selectedUseCaseIds.map((id) => {
                    const uc = useCases?.find(u => u.id === id);
                    return uc ? (
                      <Badge key={id} variant="outline" className="flex items-center gap-1">
                        <Link2 className="w-3 h-3" />
                        {uc.code && <span className="font-mono">{uc.code}</span>}
                        {uc.title}
                        <button
                          type="button"
                          onClick={() => setSelectedUseCaseIds(selectedUseCaseIds.filter(i => i !== id))}
                          className="ml-1 hover:text-red-600"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ) : null;
                  })}
                </div>
              )}
            </div>
          </div>

          {/* API Contract Editor */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">API Contract</label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setApiEndpoints([...apiEndpoints, { path: '', method: 'GET' }])}
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Endpoint
              </Button>
            </div>
            <div className="space-y-2 border rounded-lg p-3 max-h-60 overflow-y-auto">
              {apiEndpoints.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-2">No API endpoints. Click "Add Endpoint" to add one.</p>
              ) : (
                apiEndpoints.map((endpoint, index) => (
                  <div key={index} className="p-3 border rounded bg-gray-50 space-y-2">
                    <div className="flex gap-2 items-start">
                      <div className="flex-1 space-y-2">
                        <div className="flex gap-2">
                          <Select
                            value={endpoint.method}
                            onValueChange={(value: ApiEndpoint['method']) => {
                              const updated = [...apiEndpoints];
                              updated[index] = { ...updated[index], method: value };
                              setApiEndpoints(updated);
                            }}
                          >
                            <SelectTrigger className="w-32">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="GET">GET</SelectItem>
                              <SelectItem value="POST">POST</SelectItem>
                              <SelectItem value="PUT">PUT</SelectItem>
                              <SelectItem value="DELETE">DELETE</SelectItem>
                              <SelectItem value="PATCH">PATCH</SelectItem>
                            </SelectContent>
                          </Select>
                          <Input
                            placeholder="/api/endpoint"
                            value={endpoint.path}
                            onChange={(e) => {
                              const updated = [...apiEndpoints];
                              updated[index] = { ...updated[index], path: e.target.value };
                              setApiEndpoints(updated);
                            }}
                            className="flex-1"
                          />
                        </div>
                        <Input
                          placeholder="Description (optional)"
                          value={endpoint.description || ''}
                          onChange={(e) => {
                            const updated = [...apiEndpoints];
                            updated[index] = { ...updated[index], description: e.target.value };
                            setApiEndpoints(updated);
                          }}
                        />
                        <div className="grid grid-cols-2 gap-2">
                          <Textarea
                            placeholder="Request schema (optional)"
                            value={endpoint.requestSchema || ''}
                            onChange={(e) => {
                              const updated = [...apiEndpoints];
                              updated[index] = { ...updated[index], requestSchema: e.target.value };
                              setApiEndpoints(updated);
                            }}
                            rows={2}
                            className="text-sm"
                          />
                          <Textarea
                            placeholder="Response schema (optional)"
                            value={endpoint.responseSchema || ''}
                            onChange={(e) => {
                              const updated = [...apiEndpoints];
                              updated[index] = { ...updated[index], responseSchema: e.target.value };
                              setApiEndpoints(updated);
                            }}
                            rows={2}
                            className="text-sm"
                          />
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setApiEndpoints(apiEndpoints.filter((_, i) => i !== index))}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Data Contract Editor */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">Data Contract</label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setDataEntities([...dataEntities, { name: '', fields: [] }])}
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Entity
              </Button>
            </div>
            <div className="space-y-3 border rounded-lg p-3 max-h-60 overflow-y-auto">
              {dataEntities.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-2">No data entities. Click "Add Entity" to add one.</p>
              ) : (
                dataEntities.map((entity, entityIndex) => (
                  <div key={entityIndex} className="p-3 border rounded bg-gray-50 space-y-2">
                    <div className="flex gap-2 items-start">
                      <div className="flex-1 space-y-2">
                        <Input
                          placeholder="Entity name"
                          value={entity.name}
                          onChange={(e) => {
                            const updated = [...dataEntities];
                            updated[entityIndex] = { ...updated[entityIndex], name: e.target.value };
                            setDataEntities(updated);
                          }}
                        />
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <label className="text-xs font-medium text-gray-600">Fields</label>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                const updated = [...dataEntities];
                                updated[entityIndex] = {
                                  ...updated[entityIndex],
                                  fields: [...(updated[entityIndex].fields || []), { name: '', type: 'string', required: false }],
                                };
                                setDataEntities(updated);
                              }}
                            >
                              <Plus className="w-3 h-3 mr-1" />
                              Add Field
                            </Button>
                          </div>
                          {(entity.fields || []).map((field, fieldIndex) => (
                            <div key={fieldIndex} className="flex gap-2 items-center">
                              <Input
                                placeholder="Field name"
                                value={field.name}
                                onChange={(e) => {
                                  const updated = [...dataEntities];
                                  const fields = [...(updated[entityIndex].fields || [])];
                                  fields[fieldIndex] = { ...fields[fieldIndex], name: e.target.value };
                                  updated[entityIndex] = { ...updated[entityIndex], fields };
                                  setDataEntities(updated);
                                }}
                                className="flex-1"
                              />
                              <Input
                                placeholder="Type"
                                value={field.type}
                                onChange={(e) => {
                                  const updated = [...dataEntities];
                                  const fields = [...(updated[entityIndex].fields || [])];
                                  fields[fieldIndex] = { ...fields[fieldIndex], type: e.target.value };
                                  updated[entityIndex] = { ...updated[entityIndex], fields };
                                  setDataEntities(updated);
                                }}
                                className="w-24"
                              />
                              <label className="flex items-center gap-1 text-xs">
                                <input
                                  type="checkbox"
                                  checked={field.required}
                                  onChange={(e) => {
                                    const updated = [...dataEntities];
                                    const fields = [...(updated[entityIndex].fields || [])];
                                    fields[fieldIndex] = { ...fields[fieldIndex], required: e.target.checked };
                                    updated[entityIndex] = { ...updated[entityIndex], fields };
                                    setDataEntities(updated);
                                  }}
                                />
                                Required
                              </label>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  const updated = [...dataEntities];
                                  const fields = (updated[entityIndex].fields || []).filter((_, i) => i !== fieldIndex);
                                  updated[entityIndex] = { ...updated[entityIndex], fields };
                                  setDataEntities(updated);
                                }}
                              >
                                <X className="w-3 h-3" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setDataEntities(dataEntities.filter((_, i) => i !== entityIndex))}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* SLA Terms Editor */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">SLA Terms</label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setSlaTerms([...slaTerms, { metric: '', target: '' }])}
              >
                <Plus className="w-4 h-4 mr-1" />
                Add SLA Term
              </Button>
            </div>
            <div className="space-y-2 border rounded-lg p-3 max-h-60 overflow-y-auto">
              {slaTerms.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-2">No SLA terms. Click "Add SLA Term" to add one.</p>
              ) : (
                slaTerms.map((term, index) => (
                  <div key={index} className="flex gap-2 items-start p-2 border rounded bg-gray-50">
                    <div className="flex-1 space-y-2">
                      <Input
                        placeholder="Metric (e.g., Response Time)"
                        value={term.metric}
                        onChange={(e) => {
                          const updated = [...slaTerms];
                          updated[index] = { ...updated[index], metric: e.target.value };
                          setSlaTerms(updated);
                        }}
                      />
                      <div className="flex gap-2">
                        <Input
                          placeholder="Target value"
                          value={term.target}
                          onChange={(e) => {
                            const updated = [...slaTerms];
                            updated[index] = { ...updated[index], target: e.target.value };
                            setSlaTerms(updated);
                          }}
                          className="flex-1"
                        />
                        <Input
                          placeholder="Unit (optional)"
                          value={term.unit || ''}
                          onChange={(e) => {
                            const updated = [...slaTerms];
                            updated[index] = { ...updated[index], unit: e.target.value };
                            setSlaTerms(updated);
                          }}
                          className="w-24"
                        />
                      </div>
                      <Input
                        placeholder="Measurement method (optional)"
                        value={term.measurement || ''}
                        onChange={(e) => {
                          const updated = [...slaTerms];
                          updated[index] = { ...updated[index], measurement: e.target.value };
                          setSlaTerms(updated);
                        }}
                      />
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setSlaTerms(slaTerms.filter((_, i) => i !== index))}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))
              )}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
